import React, { memo, useState, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useSelector, shallowEqual } from 'react-redux'
import { createSelector } from 'reselect'
import { pathToRegexp } from 'path-to-regexp'

import { Button, Grid, Typography, makeStyles } from '@material-ui/core'
import Skeleton from '@material-ui/lab/Skeleton'

import SearchField from '~/containers/SearchField'
import DialogRetailForm from '~/components/DialogRetailForm'
import useViewportSize from '~/components/useViewportSize'
import AddBusinessFilledIcon from '~/components/AddBusinessFilledIcon'
import { useLiterals } from '~/components/Literals'

import ProductHeader from './ProductHeader'
import HeaderSearchWrapper from './HeaderSearchWrapper'
import PositionButton from './PositionButton'
import { SearchProductVirtualized } from './SearchProductVirtualized'

import { TYPES } from '~/actions'
import track from '~/track'
import { useIsInIframe } from '../useIsInIframe'
import { selectStores } from '~/selectors'

const selector = createSelector(
  (state) => state.app.view,
  (state) => state.app.currentProduct,
  (state) => state.config,
  (...args) => args,
)

const useStyles = makeStyles((theme) => ({
  boxImage: {
    width: '100%',
    height: 40,
    display: 'flex',
    alignItems: 'center',
  },
  customerBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 50,
    width: '100%',
    backgroundColor: '#FFF',
    margin: 0,
    padding: '6px 12px',
  },
  resselButton: {
    width: '100%',
    color: theme.palette.primary.main,

    [theme.breakpoints.up('md')]: {
      backgroundColor: '#F2F2F2',
      width: 'fit-content',
      position: 'fixed',
      top: 0,
      right: 0,
      zIndex: 1013,
      borderRadius: 0,
      paddingInline: 16,
      '&:hover': {
        backgroundColor: '#EEEBEB',
      },
    },
  },
  inputSearch: {
    padding: 8,
  },
  boxSearchProduct: {
    position: 'relative',
    backgroundColor: theme.palette.secondary.main,
    padding: 16,
  },
}))

const isRouterStore = pathToRegexp(['/loja/:cnpj'], null, { sensitive: true })

function Header({ config, position, handleToggle, searchFieldHaveBeenFocus, setSearchFieldHaveBeenFocus }) {
  const isInIframe = useIsInIframe()
  const classes = useStyles()
  const viewport = useViewportSize()
  const location = useLocation()
  const { getLiteral } = useLiterals()

  const [retailOpen, setRetailOpen] = useState(false)
  const [view, product] = useSelector(selector, shallowEqual)
  const { showStoreInformation } = useSelector(selectStores)

  const isMobile = viewport.width < 930
  const viewTypes = [
    TYPES.VIEW_PRODUCTS,
    TYPES.VIEW_SEARCH_PRODUCTS,
    TYPES.VIEW_EMPTY,
    TYPES.VIEW_STORE_DETAILS,
    TYPES.VIEW_STORES,
  ]

  let FORM_WAS_SEND = false

  const handleRetail = () => {
    track('queroRevender', { category: 'menu' })
    setRetailOpen(true)
  }

  const handleRetailDone = () => {
    FORM_WAS_SEND = true
    setRetailOpen(false)
  }

  const { hideRetailButton, retailLink, retailLabel, cnpj, generic, showContextViewStore } = config
  const enableRetailButton = position.customLocation || position.user

  const resselButtonComponent = useMemo(() => {
    if (showStoreInformation && isRouterStore.test(location.pathname) && !isMobile) return null

    return (
      !FORM_WAS_SEND &&
      !hideRetailButton &&
      (generic || !cnpj ? (
        <Button
          className={!['VIEW_EMPTY'].includes(view) ? classes.resselButton : ''}
          color={['VIEW_EMPTY'].includes(view) ? 'primary' : 'inherit'}
          variant="text"
          target="_blank"
          href={retailLink || 'https://empresas.gofind.online/'}
          onClick={() =>
            track('souEmpresa', {
              name: retailLabel || getLiteral('2056'),
              category: 'menu',
            })
          }
        >
          {retailLabel || getLiteral('2056')}
        </Button>
      ) : retailLink ? (
        <Button
          className={!['VIEW_EMPTY'].includes(view) ? classes.resselButton : ''}
          color={['VIEW_EMPTY'].includes(view) ? 'primary' : 'inherit'}
          variant="text"
          target="_blank"
          href={retailLink}
          onClick={() => track('queroRevender', { name: retailLink, category: 'menu' })}
          disabled={!enableRetailButton}
        >
          {getLiteral('1870')}
        </Button>
      ) : (
        <Button
          className={!['VIEW_EMPTY'].includes(view) ? classes.resselButton : ''}
          color={['VIEW_EMPTY'].includes(view) ? 'primary' : 'inherit'}
          data-cy="retail-button"
          variant="text"
          onClick={handleRetail}
          disabled={!enableRetailButton}
          startIcon={<AddBusinessFilledIcon className={classes.iconColor} />}
        >
          {getLiteral('1870')}
        </Button>
      ))
    )
  }, [
    FORM_WAS_SEND,
    retailLink,
    hideRetailButton,
    cnpj,
    generic,
    view,
    isMobile,
    showStoreInformation,
    location.pathname,
  ])

  return (
    <>
      <DialogRetailForm open={retailOpen} setRetailOpen={setRetailOpen} onDone={handleRetailDone} position={position} />

      {!isInIframe && (viewTypes.includes(view) || !isMobile) && (
        <Grid
          className={classes.customerBar}
          // style={{
          //   justifyContent: view === 'VIEW_STORES' && !isMobile ? 'space-between' : 'center'
          // }}
        >
          <Link to={config.showContextViewStore ? '/produto/todos' : '/'}>
            <Grid className={classes.boxImage}>
              <img style={{ maxHeight: '100%', maxWidth: '100%' }} src={config.imageUrl} alt={getLiteral('1012')} />
            </Grid>
          </Link>
          {!isMobile && <Grid>{resselButtonComponent}</Grid>}
        </Grid>
      )}

      <PositionButton classes={classes} position={position} resselButtonComponent={resselButtonComponent} />

      {view === TYPES.VIEW_STORES || view === TYPES.VIEW_STORE_DETAILS ? (
        product ? (
          <ProductHeader
            product={product}
            view={view}
            handleToggle={handleToggle}
            position={position}
            config={config}
          />
        ) : (
          <Grid style={{ padding: 15 }}>
            <Skeleton variant="rect" width="100%" height={50} />
          </Grid>
        )
      ) : (
        <Grid className={classes.boxSearchProduct} style={{ zIndex: searchFieldHaveBeenFocus ? 1002 : 0 }}>
          <Typography variant="body1" style={{ fontSize: 18, color: '#FFF', marginBottom: 8 }}>
            {getLiteral('2819')}
          </Typography>
          {!config.showContextViewEstablishment && (
            <Grid {...(isMobile && { className: classes.inputSearch })}>
              <HeaderSearchWrapper position={position} config={config}>
                {showContextViewStore ? (
                  <SearchProductVirtualized product={product} position={position} config={config} />
                ) : (
                  <SearchField position={position} setSearchFieldHaveBeenFocus={setSearchFieldHaveBeenFocus} />
                )}
              </HeaderSearchWrapper>
            </Grid>
          )}
        </Grid>
      )}
    </>
  )
}

export default memo(Header)
